import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { hasPermission, inScope, isSuperuser } from "@/lib/permissions";
import { AlertBell } from "@/components/AlertBell";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { TextScaleControls } from "@/components/TextScaleControls";
import { AskErio } from "@/components/AskErio";
import { SystemStatus } from "@/components/SystemStatus";
import { useI18n } from "@/context/TranslationContext";
import { useTour } from "@/components/GuidedTour";
import { WhatsNewBanner } from "@/components/WhatsNewBanner";
import { LogOut, ArrowLeft, BookOpen, ChevronDown, Crown, ShieldCheck, Sparkles } from "lucide-react";

const ROLE_LABEL = {
  admin: "Administrator",
  org_admin: "Org Administrator",
  executive: "Executive",
  manager: "Manager",
  contributor: "Contributor",
  viewer: "Viewer",
  cro: "Chief Risk Officer",
  ciso: "CISO",
  cpo: "Chief Procurement Officer",
  cfo: "Chief Financial Officer",
};

const NAV_PRIMARY = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/risks", label: "Risks", permission: "risk.read" },
  { to: "/suppliers", label: "Suppliers", permission: "supplier.read", domain: "t_thirdparty" },
  { to: "/signals", label: "Signals", permission: "signal.read" },
  { to: "/manual", label: "Guide" },
];

const NAV_GROUPS = [
  {
    label: "Intelligence",
    links: [
      { to: "/trends", label: "Trends", permission: "risk.read" },
      { to: "/geopolitics", label: "Geopolitics", permission: "risk.read", domain: "t_geopolitical" },
      { to: "/calibration", label: "Sector calibration", permission: "risk.read" },
      { to: "/organisation", label: "Organisation profile", permission: "risk.read" },
      { to: "/ingestion", label: "Data sources", permission: "signal.read" },
    ],
  },
  {
    label: "Governance",
    links: [
      { to: "/frameworks", label: "Frameworks", permission: "framework.read" },
      { to: "/compliance", label: "Compliance", permission: "framework.read" },
      { to: "/control-roi", label: "Control ROI", permission: "risk.read" },
      { to: "/risk-register-quality", label: "Risk register quality", permission: "risk.read" },
      { to: "/plan-integrity", label: "Plan integrity", permission: "risk.read" },
      { to: "/plan-portfolio", label: "Plan portfolio", permission: "risk.read" },
      { to: "/programmes", label: "Saved programmes", permission: "risk.read" },
      { to: "/appetite", label: "Risk appetite", permission: "risk.read" },
      { to: "/governance", label: "Trust Centre", permission: "attestation.read" },
      { to: "/audit-log", label: "Audit log", permission: "audit.read" },
      { to: "/resilience", label: "Resilience", permission: "risk.read", domain: "t_operational" },
      { to: "/scenario-studio", label: "Scenario Studio", permission: "risk.read" },
      { to: "/remediation", label: "Remediation", anyPermission: ["risk.read", "remediation.read"] },
    ],
  },
  {
    label: "Playbooks",
    links: [
      { to: "/playbooks", label: "Playbooks", permission: "playbook.read" },
      { to: "/playbook-library", label: "Library", roles: ["admin", "cro"] },
    ],
  },
  {
    label: "Platform",
    links: [
      { to: "/onboarding", label: "Import data", anyPermission: ["risk.write", "ntier.write"] },
      { to: "/interop", label: "Interop fabric", permission: "framework.read" },
      { to: "/integrations", label: "Integrations", permission: "integration.manage" },
      { to: "/connectors", label: "PPM connectors", roles: ["admin", "org_admin"] },
      { to: "/leads", label: "Leads (CRM)", superuser: true },
      { to: "/tenants", label: "Tenants", superuser: true },
      { to: "/sales-docs", label: "Sales & partner docs", superuser: true },
      { to: "/reporting", label: "Reporting", superuser: true },
      { to: "/compliance-ops", label: "Compliance ops", superuser: true },
      { to: "/strategic-ops", label: "Strategic ops", superuser: true },
      { to: "/compliance-readiness", label: "Compliance readiness", superuser: true },
      { to: "/provisioning", label: "Provisioning (SCIM)", superuser: true },
      { to: "/users", label: "Users & access", superuser: true },
      { to: "/access-policy", label: "Access policy", roles: ["admin", "org_admin"] },
      { to: "/methodology", label: "Methodology (proprietary)", superuser: true },
      { to: "/admin", label: "Admin", superuser: true },
    ],
  },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { tx } = useI18n();
  const { startTour } = useTour();
  const location = useLocation();
  const navigate = useNavigate();
  const [openGroup, setOpenGroup] = useState(null);

  if (!user) return null;

  const canSee = (l) => {
    if (l.superuser && !isSuperuser(user)) return false;
    if (l.roles && !l.roles.includes(user.role)) return false;
    if (l.permission && !hasPermission(user, l.permission)) return false;
    if (l.anyPermission && !l.anyPermission.some((p) => hasPermission(user, p))) return false;
    if (l.domain && !inScope(user, l.domain)) return false;
    return true;
  };
  const primary = NAV_PRIMARY.filter(canSee);
  const groups = NAV_GROUPS
    .map((g) => ({ ...g, links: g.links.filter(canSee) }))
    .filter((g) => g.links.length > 0);

  const linkClass = (active) =>
    `rounded-sm px-4 py-2 text-sm font-medium transition-colors ${
      active ? "bg-ek-purple/20 text-ek-lilac" : "text-white/55 hover:text-white"
    }`;

  return (
    <div className="sticky top-0 z-40">
      <a
        href="#main-content"
        onClick={(e) => {
          e.preventDefault();
          const m = document.querySelector("main");
          if (m) { m.setAttribute("tabindex", "-1"); m.focus(); m.scrollIntoView(); }
        }}
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-ek-ink focus:shadow-lg"
        data-testid="skip-to-content"
      >
        Skip to content
      </a>
      <ImpersonationBanner />
      <WhatsNewBanner />
      {/* Persistent tether to the parent brand */}
      <a
        href="https://www.ekselens.com"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="back-to-ekselens"
        className="flex h-8 items-center justify-center gap-1.5 bg-[#0b0b0c] text-[0.7rem] font-medium tracking-wide text-white/65 transition-colors hover:text-white"
      >
        <ArrowLeft size={11} /> Back to Ekselens Consulting
      </a>

      <header className="border-b border-white/10 bg-black" data-testid="navbar">
        <div className="flex h-16 items-center justify-between gap-4 px-6">
          <div className="flex min-w-0 items-center gap-6">
            <Link to="/dashboard" data-testid="navbar-logo-link">
              <Logo dark />
            </Link>
            <nav className="hidden items-center gap-1 lg:flex">
              {primary.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`navlink-${l.to.slice(1)}`}
                  className={linkClass(location.pathname === l.to)}>
                  {tx(l.label)}
                </Link>
              ))}
              {groups.map((g) => {
                const active = g.links.some((l) => location.pathname === l.to);
                return (
                  <div key={g.label} className="relative"
                    onMouseEnter={() => setOpenGroup(g.label)}
                    onMouseLeave={() => setOpenGroup((o) => (o === g.label ? null : o))}>
                    <button
                      data-testid={`navgroup-${g.label.toLowerCase()}`}
                      onClick={() => setOpenGroup((o) => (o === g.label ? null : g.label))}
                      className={`${linkClass(active)} flex items-center gap-1`}>
                      {tx(g.label)}
                      <ChevronDown size={13} className={`transition-transform ${openGroup === g.label ? "rotate-180" : ""}`} />
                    </button>
                    {openGroup === g.label && (
                      <div className="absolute left-0 top-full min-w-[200px] rounded-md border border-white/10 bg-[#0b0b0c] p-1.5 shadow-xl"
                        data-testid={`navgroup-panel-${g.label.toLowerCase()}`}>
                        {g.links.map((l) => (
                          <Link key={l.to} to={l.to} data-testid={`navlink-${l.to.slice(1)}`}
                            onClick={() => setOpenGroup(null)}
                            className={`block rounded-sm px-3 py-2 text-sm transition-colors ${
                              location.pathname === l.to ? "bg-ek-purple/20 text-ek-lilac" : "text-white/60 hover:bg-white/5 hover:text-white"
                            }`}>
                            {tx(l.label)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <span
              data-testid="navbar-tier-badge"
              title={isSuperuser(user) ? "You are signed in as the system Owner — full live-operations access" : "Standard access — owner-only functions are hidden"}
              className={`hidden items-center gap-1 rounded-full px-2.5 py-1 text-[0.65rem] font-bold uppercase tracking-wide xl:inline-flex ${
                isSuperuser(user) ? "bg-ek-purple text-white" : "bg-white/10 text-white/70"
              }`}
            >
              {isSuperuser(user) ? <Crown size={11} /> : <ShieldCheck size={11} />}
              {isSuperuser(user) ? "Owner" : (ROLE_LABEL[user.role] || user.role)}
            </span>
            <LanguageSwitcher />
            <TextScaleControls />
            <SystemStatus />
            <button
              onClick={startTour}
              title="Take the guided tour"
              data-testid="nav-take-tour"
              className="hidden items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-medium text-white/80 transition-colors hover:border-ek-purple hover:text-white xl:inline-flex"
            >
              <Sparkles size={13} /> Tour
            </button>
            <Link
              to="/help"
              title="Help Centre — user manual"
              data-testid="navbar-help-link"
              className="hidden h-9 w-9 items-center justify-center rounded-sm border border-white/15 text-white/60 transition-colors hover:border-ek-lilac hover:text-ek-lilac xl:inline-flex"
            >
              <BookOpen size={16} />
            </Link>
            {hasPermission(user, "risk.read") && <AlertBell />}
            <div className="hidden max-w-[180px] text-right xl:block">
              <div className="truncate text-sm font-semibold text-white" data-testid="navbar-user-email">
                {user.email}
              </div>
              <div className="text-xs text-white/65" data-testid="navbar-user-role">
                {ROLE_LABEL[user.role] || user.role}
              </div>
            </div>
            <Link
              to="/security"
              data-testid="navbar-security-link"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-white/15 text-white/60 transition-colors hover:border-ek-lilac hover:text-ek-lilac"
              title="Security & two-factor authentication"
            >
              <ShieldCheck size={16} />
            </Link>
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="logout-button"
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-sm border border-white/15 px-2.5 text-xs font-semibold text-white/70 transition-colors hover:border-ek-lilac hover:text-ek-lilac"
              title="Sign out of ERIO"
              aria-label="Sign out"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>
      <AskErio />
    </div>
  );
};
